import React, { useState } from 'react';
import { DB } from '../data/database';

/**
 * Public unit page — shown when someone scans a QR code and is NOT logged in.
 * Shows only safe public info: images, specs, status, contact buttons.
 * No pricing, no chassis, no internal data.
 */

function getCompanyInfo() {
  try {
    // Try DB first
    const cfg = DB.get('appConfig');
    if (cfg && typeof cfg === 'object' && !Array.isArray(cfg) && cfg.company_info) return cfg.company_info;
    // Fallback: read directly from localStorage
    const raw = localStorage.getItem('bigstar_appConfig');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.company_info) return parsed.company_info;
    }
  } catch {}
  return {};
}

function PublicUnitView({ unit, onLogin }) {
  const [lightbox, setLightbox] = useState(null);
  const co = getCompanyInfo();
  const logoUrl = co.logo_url || '';
  const phone = co.phone || '+971565665400';
  const email = co.email || 'info@bigstar.com';
  const whatsappNum = phone.replace(/[^\d]/g, '');

  const name = unit.make ? `${unit.year ? unit.year + ' ' : ''}${unit.make} ${unit.model || ''}`.trim() : unit.engineName || unit.partName || 'Unit';
  const isSold = ['sold', 'sold_awaiting_payment', 'delivered', 'dismantled'].includes(unit.status);
  const images = unit.images || [];

  const waMessage = encodeURIComponent(
    isSold
      ? `Hello, I'm inquiring about a unit I saw at your yard (it appears to be sold). Do you have similar units available?`
      : `Hello, I'm interested in the ${name} I saw at your yard. Can you provide more details and pricing? Thank you!`
  );

  const specs = [
    ['Year', unit.year],
    ['Grade', unit.grade || unit.score],
    ['Mileage', unit.mileage ? `${Number(unit.mileage).toLocaleString()} km` : null],
    ['Transmission', unit.transmission],
    ['Drive', unit.driveSystem],
    ['Steering', unit.steer],
    ['Color', unit.color],
    ['Fuel', unit.fuel || unit.fuelType],
    ['Displacement', unit.displacement],
    ['Seats', unit.seats],
    ['Category', unit.category],
    ['Condition', unit.condition],
  ].filter(([, v]) => v);

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117', color: '#f0f6fc', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ background: '#161b22', borderBottom: '1px solid #30363d', padding: '16px 20px', textAlign: 'center' }}>
        {logoUrl
          ? <img src={logoUrl} alt="Logo" style={{ height: 44, objectFit: 'contain' }} />
          : <div style={{ fontSize: 22, fontWeight: 800, color: '#4ade80' }}>★ BIG STAR</div>
        }
        <div style={{ fontSize: 11, color: '#8b949e', marginTop: 2 }}>{co.tagline || 'Used Cars & Spare Parts'}</div>
      </div>

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '20px 16px' }}>

        {/* Main image */}
        {images.length > 0 && (
          <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 16, background: '#161b22' }}>
            <img src={images[0]} alt={name} style={{ width: '100%', height: 260, objectFit: 'cover', cursor: 'pointer' }}
              onClick={() => setLightbox(0)} />
          </div>
        )}

        {/* Unit name + status */}
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px', color: '#f0f6fc' }}>{name}</h1>
        <div style={{ marginBottom: 16 }}>
          {isSold
            ? <span style={{ fontSize: 13, fontWeight: 700, color: '#f85149', background: 'rgba(248,81,73,0.12)', padding: '4px 12px', borderRadius: 6 }}>SOLD</span>
            : <span style={{ fontSize: 13, fontWeight: 700, color: '#3fb950', background: 'rgba(63,185,80,0.12)', padding: '4px 12px', borderRadius: 6 }}>Available</span>
          }
        </div>

        {/* Specs grid */}
        {specs.length > 0 && (
          <div style={{ background: '#161b22', borderRadius: 10, border: '1px solid #30363d', padding: '12px 16px', marginBottom: 16 }}>
            {specs.map(([label, value], i) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < specs.length - 1 ? '1px solid #30363d' : 'none', fontSize: 14 }}>
                <span style={{ color: '#8b949e' }}>{label}</span>
                <span style={{ fontWeight: 600 }}>{value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Photo gallery */}
        {images.length > 1 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Photos</div>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
              {images.map((img, i) => (
                <img key={i} src={img} alt="" style={{ width: 90, height: 68, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', flexShrink: 0, border: '1px solid #30363d' }}
                  onClick={() => setLightbox(i)} />
              ))}
            </div>
          </div>
        )}

        {/* Sold message */}
        {isSold && (
          <div style={{ padding: 16, background: 'rgba(248,81,73,0.06)', border: '1px solid rgba(248,81,73,0.2)', borderRadius: 10, marginBottom: 16, textAlign: 'center', fontSize: 13, color: '#f85149' }}>
            This unit has been sold. Contact us for similar units.
          </div>
        )}

        {/* Contact buttons */}
        <div style={{ fontSize: 12, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Interested?</div>

        <a href={`https://wa.me/${whatsappNum}?text=${waMessage}`} target="_blank" rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', background: '#25d366', color: '#fff', borderRadius: 10, textDecoration: 'none', fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
          <span style={{ fontSize: 22 }}>📱</span>
          <div><div>WhatsApp Us</div><div style={{ fontSize: 11, fontWeight: 400, opacity: 0.85 }}>Contact us about this unit</div></div>
        </a>

        <a href={`mailto:${email}?subject=${encodeURIComponent(`Inquiry: ${name}`)}`}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', background: '#161b22', color: '#f0f6fc', border: '1px solid #30363d', borderRadius: 10, textDecoration: 'none', fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
          <span style={{ fontSize: 20 }}>📧</span>
          <div><div>Email Us</div><div style={{ fontSize: 11, fontWeight: 400, color: '#8b949e' }}>{email}</div></div>
        </a>

        <a href={`tel:${phone}`}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', background: '#161b22', color: '#f0f6fc', border: '1px solid #30363d', borderRadius: 10, textDecoration: 'none', fontWeight: 600, fontSize: 14, marginBottom: 20 }}>
          <span style={{ fontSize: 20 }}>📞</span>
          <div><div>Call Us</div><div style={{ fontSize: 11, fontWeight: 400, color: '#8b949e' }}>{phone}</div></div>
        </a>

        {/* Login button */}
        <div style={{ borderTop: '1px solid #30363d', paddingTop: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: '#8b949e', marginBottom: 10, textAlign: 'center' }}>Already a BIG STAR customer?</div>
          <button onClick={onLogin}
            style={{ width: '100%', padding: '14px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
            🔐 Log In to Your Account
          </button>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', fontSize: 11, color: '#6e7681', padding: '16px 0', borderTop: '1px solid #30363d' }}>
          <div style={{ fontWeight: 600 }}>{co.name || 'BIG STAR Trading LLC'}</div>
          <div>{co.tagline || 'Used Cars & Spare Parts'}</div>
          {co.address && <div>{co.address}</div>}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setLightbox(null)}>
          <button style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer' }} onClick={() => setLightbox(null)}>✕</button>
          {lightbox > 0 && <button style={{ position: 'absolute', left: 16, background: 'none', border: 'none', color: '#fff', fontSize: 32, cursor: 'pointer' }} onClick={e => { e.stopPropagation(); setLightbox(lightbox - 1); }}>‹</button>}
          {lightbox < images.length - 1 && <button style={{ position: 'absolute', right: 16, background: 'none', border: 'none', color: '#fff', fontSize: 32, cursor: 'pointer' }} onClick={e => { e.stopPropagation(); setLightbox(lightbox + 1); }}>›</button>}
          <img src={images[lightbox]} alt="" style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8 }} onClick={e => e.stopPropagation()} />
          <div style={{ position: 'absolute', bottom: 16, color: '#8b949e', fontSize: 12 }}>{lightbox + 1} / {images.length}</div>
        </div>
      )}
    </div>
  );
}

export default PublicUnitView;
